import { Card } from "@/modern/components/ui/card";
import { cn } from "@/modern/utils";
import { X, Filter } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/modern/components/ui/button";
import { STATE_ID_TO_VIEW_NAME } from "@/other/feature/views/widget/ViewStateGridMapping.jsx";
import {
  getFilters,
  getSorters,
  getGroupers,
  getFilterValue,
  getColumnNameByField
} from "@/other/utils/viewStateUtils.jsx";
import { isObjDeepEqual, pickKeysInObject } from "@/other/utils/utils.jsx";
import ViewViewStateProcessor from "@/other/feature/views/viewStateProcessors/ViewViewStateProcessor.js";
import { PLUGINS_KEYS } from "@/other/constants/PluginKeys.jsx";

interface ViewStateItem {
  itemPath: {
    path: string;
    pathIdx?: number;
    id?: string;
    field?: string;
    type: string;
  };
  gridName: string;
  gridColumn: string;
  gridColumnFilterValue: string;
  viewType: string;
  isDisabled?: boolean;
  isCaseSensitive?: boolean;
}

interface ViewStateInspectionProps {
  record: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  context: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  canUseRecordViewState?: boolean;
  hideRemoveButtons?: boolean;
  className?: string;
}

const loadFilters = (id: string, gridType: string, gridColumns: any[], store: any, viewType: string) => { // eslint-disable-line @typescript-eslint/no-explicit-any
  return getFilters(store.filters || []).map((filterItem: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const columnName = getColumnNameByField(gridColumns, filterItem.property) || "?";
    const gridColumnFilterValue = `Filter on "${getFilterValue(filterItem)}"`;
    return {
      itemPath: { path: id, pathIdx: filterItem.pathIdx, id: filterItem.id, type: "filter" },
      gridName: gridType,
      gridColumn: columnName,
      gridColumnFilterValue,
      isDisabled: filterItem.disabled,
      isCaseSensitive: filterItem.type === "string" && filterItem.value ? filterItem.caseSensitive : false,
      viewType
    };
  });
};

const loadSorters = (id: string, gridType: string, gridColumns: any[], store: any, viewType: string) => { // eslint-disable-line @typescript-eslint/no-explicit-any
  return getSorters(store.sorters || []).map((sorterItem: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const columnName = getColumnNameByField(gridColumns, sorterItem.field) || "?";
    const isAscending = sorterItem.ascending;
    const gridColumnFilterValue = `Sort ${isAscending ? "Ascending" : "Descending"}`;
    return {
      itemPath: { path: id, pathIdx: sorterItem.pathIdx, field: sorterItem.field, type: "sorter" },
      gridName: gridType,
      gridColumn: columnName,
      gridColumnFilterValue,
      viewType
    };
  });
};

const loadGroupers = (id: string, gridType: string, gridColumns: any[], store: any, viewType: string) => { // eslint-disable-line @typescript-eslint/no-explicit-any
  return getGroupers(store.groupers || []).map((grouperItem: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
    const columnName = getColumnNameByField(gridColumns, grouperItem.field) || "?";
    const isStatefulGrouper = grouperItem.custom;
    const gridColumnFilterValue = isStatefulGrouper
      ? "Group by Settings"
      : grouperItem.ascending
        ? "Group Ascending"
        : "Group Descending";
    return {
      itemPath: { path: id, pathIdx: grouperItem.pathIdx, field: grouperItem.field, type: "grouper" },
      gridName: gridType,
      gridColumn: columnName || "?",
      gridColumnFilterValue,
      viewType
    };
  });
};

const viewViewStateProcessorColumnsKeys = (() => {
  const item = ViewViewStateProcessor.statesToIncludeArray.find(
    (item: any) => typeof item === "object" && item.name === "columns" // eslint-disable-line @typescript-eslint/no-explicit-any
  );
  return item && typeof item === "object" && "statesToInclude" in item ? item.statesToInclude : [];
})();

const cleanColumnsForViewState = (columns: any[]) => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const keys = Array.isArray(viewViewStateProcessorColumnsKeys) &&
    viewViewStateProcessorColumnsKeys.every((k: any) => typeof k === "string") // eslint-disable-line @typescript-eslint/no-explicit-any
    ? viewViewStateProcessorColumnsKeys as string[]
    : [];
  return columns.map((col) => pickKeysInObject(col, keys));
};

const loadColumnChanges = (id: string, gridType: string, viewType: string, context: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const { viewManager } = context;
  const changes = [];
  if (viewManager.pageViewsRef && viewManager.pageViewsRef[viewType]) {
    const widget = viewManager.pageViewsRef[viewType];
    const defaultState = cleanColumnsForViewState(widget.defaultState.columns);
    const currentState = cleanColumnsForViewState(widget.state.columns);

    const diff = !isObjDeepEqual(defaultState, currentState);
    if (diff) {
      changes.push({
        itemPath: { path: id, type: "column-changes" },
        gridName: gridType,
        gridColumn: `Miscellaneous`,
        gridColumnFilterValue: "Columns Modified",
        viewType
      });
    }
  }
  return changes;
};

const timelineKeysInFeatures = [PLUGINS_KEYS.swivuTimelineConfigurator, "timeRanges"];

const loadTimeLineChanges = (id: string, gridType: string, viewType: string, context: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
  const { viewManager } = context;
  const changes = [];
  if (viewManager.pageViewsRef && viewManager.pageViewsRef[viewType]) {
    const widget = viewManager.pageViewsRef[viewType];
    if (widget.isSchedulerPro && widget.features) {
      const defaultState = widget.defaultState;
      const currentState = widget.state;

      const changeConfig = {
        gridName: gridType,
        gridColumn: `Miscellaneous`,
        viewType
      };

      if (defaultState.zoomLevel !== currentState.zoomLevel) {
        changes.push({
          ...changeConfig,
          itemPath: { path: id, type: "timeline-zoom-level-change" },
          gridColumnFilterValue: "Zoom level Modified"
        });
      }

      if (defaultState.tickSize !== currentState.tickSize) {
        changes.push({
          ...changeConfig,
          itemPath: { path: id, type: "timeline-tick-size-change" },
          gridColumnFilterValue: "Ticksize Modified"
        });
      }

      if (currentState.features) {
        const cleanedDefaultTimelineState = pickKeysInObject(
          widget.swivuSchedulerDefaultState.features,
          timelineKeysInFeatures
        );
        const cleanedCurrentTimelineState = pickKeysInObject(currentState.features, timelineKeysInFeatures);

        if (!isObjDeepEqual(cleanedDefaultTimelineState, cleanedCurrentTimelineState)) {
          changes.push({
            ...changeConfig,
            itemPath: { path: id, type: "timeline-features-change" },
            gridColumnFilterValue: "Timeline Configuration Modified"
          });
        }
      }
    }
  }
  return changes;
};

export const ViewStateInspection = ({
  record,
  context,
  canUseRecordViewState,
  hideRemoveButtons = false,
  className
}: ViewStateInspectionProps) => {
  const [viewStateData, setViewStateData] = useState<ViewStateItem[]>([]);

  useEffect(() => {
    if (typeof record.viewState === "object" && Object.keys(record.viewState).length) {
      let viewState;
      if (canUseRecordViewState) {
        viewState = (record.isDefaultView ? record.viewState.customViewState : record.viewState) ?? {};
      } else {
        viewState = context.viewManager.getCurrentDefaultViewStates();
      }

      const viewStates = Object.entries(viewState)
        .filter(([_viewType, viewState]) => Boolean((viewState as any)?.store)) // eslint-disable-line @typescript-eslint/no-explicit-any
        .map(([viewType, viewState]) => {
          const viewConfig = STATE_ID_TO_VIEW_NAME[`${record.viewType}-${viewType}`];
          return {
            id: `${viewType}.store`,
            gridType: viewConfig.title,
            gridColumns: viewConfig.config.columns,
            store: (viewState as any).store, // eslint-disable-line @typescript-eslint/no-explicit-any
            viewType
          };
        });

      if (viewStates.length) {
        const data = viewStates.reduce((accum: ViewStateItem[], vs: any) => { // eslint-disable-line @typescript-eslint/no-explicit-any
          const columns = vs.gridColumns.reduce((columnsWithChildren: any[], column: any) => // eslint-disable-line @typescript-eslint/no-explicit-any
            columnsWithChildren.concat(column.children || column), []
          );
          const sorters = loadSorters(`${vs.id}.sorters`, vs.gridType, columns, vs.store, vs.viewType);
          const filters = loadFilters(`${vs.id}.filters`, vs.gridType, columns, vs.store, vs.viewType);
          const groupers = loadGroupers(`${vs.id}.groupers`, vs.gridType, columns, vs.store, vs.viewType);
          const columnChanges = loadColumnChanges(`${vs.id}.column-changes`, vs.gridType, vs.viewType, context);
          const timelineChanges = loadTimeLineChanges(`${vs.id}.timeline-changes`, vs.gridType, vs.viewType, context);
          return accum.concat(filters, sorters, groupers, columnChanges, timelineChanges);
        }, []);
        setViewStateData(data);
      } else {
        setViewStateData([]);
      }
    } else {
      setViewStateData([]);
    }
  }, [record, context, canUseRecordViewState]);

  const handleRemoveState = (item: ViewStateItem) => {
    const { itemPath, viewType } = item;
    const pageRefs = context.viewManager.pageViewsRef;
    const widget = pageRefs[viewType];

    const actionMap: Record<string, (widget: any, itemPath: any) => void> = { // eslint-disable-line @typescript-eslint/no-explicit-any
      grouper: (widget, itemPath) => widget.store.removeGrouper(itemPath.field),
      sorter: (widget, itemPath) => widget.store.removeSorter(itemPath.field),
      filter: (widget, itemPath) => widget.store.removeFilter(itemPath.id),
      "column-changes": (widget) => {
        widget.state = { ...widget.state, columns: widget.defaultState.columns };
      },
      "timeline-zoom-level-change": (widget) => {
        widget.state = { ...widget.state, zoomLevel: widget.defaultState.zoomLevel };
      },
      "timeline-tick-size-change": (widget) => {
        widget.state = { ...widget.state, tickSize: widget.defaultState.tickSize };
      },
      "timeline-features-change": (widget) => {
        widget.state = { ...widget.state, features: widget.swivuSchedulerDefaultState.features };
      }
    };

    actionMap[itemPath.type]?.(widget, itemPath);

    setViewStateData((prev) => prev.filter((stateItem) => stateItem !== item));
  };

  // Group by viewType
  const groupedData = viewStateData.reduce((acc, item) => {
    if (!acc[item.viewType]) {
      acc[item.viewType] = [];
    }
    acc[item.viewType].push(item);
    return acc;
  }, {} as Record<string, ViewStateItem[]>);

  if (viewStateData.length === 0) {
    return (
      <Card className={cn("flex h-full flex-col items-center justify-center gap-3 bg-transparent p-6 border-none", className)}>
        <Filter className="h-12 w-12 text-muted-foreground/40" />
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">No View State Applied</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Apply filters, sorters, or groupers on the view to see changes here
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={cn("py-3 px-4 overflow-y-auto", className)}>
      <div className="space-y-2 overflow-y-auto">
        {Object.entries(groupedData).map(([viewType, items]) => (
          <div key={viewType} className="space-y-1">
            <div className="sticky top-0 z-10 bg-background/95 px-3 py-2 backdrop-blur">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-primary">
                {viewType.replace("-", " ")}
              </h4>
            </div>
            <div className="space-y-1">
              {items.map((item, idx) => (
                <div
                  key={`${item.viewType}-${idx}`}
                  className="group flex items-center justify-between gap-2 rounded-lg border border-border/40 bg-card px-3 py-2 text-sm transition-colors hover:border-border hover:bg-accent/50"
                >
                  <span className="flex-1 text-foreground">
                    {item.gridColumn ? `${item.gridColumn}: ${item.gridColumnFilterValue}` : "No View State"}
                  </span>
                  {!canUseRecordViewState && !hideRemoveButtons && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100"
                      onClick={() => handleRemoveState(item)}
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};
